"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Map, Pencil, Trash2, ChevronRight } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function ZoneCard({
  name,
  planType,
  planBlob,
  mesureCount,
  onEdit,
  onDelete,
  onClick,
  dragHandle,
}: {
  name: string;
  planType: string;
  planBlob?: ArrayBuffer | Blob;
  mesureCount?: number;
  onEdit: () => void;
  onDelete: () => void;
  onClick?: () => void;
  /** Poignée de drag (optionnelle) rendue à gauche du titre. Fournie par le
   *  wrapper sortable ; absente, la carte s'affiche normalement. */
  dragHandle?: ReactNode;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (planBlob) {
      // Convertir ArrayBuffer → Blob si nécessaire (Safari iOS)
      const blob = planBlob instanceof Blob ? planBlob : new Blob([planBlob], { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [planBlob]);

  return (
    <Card
      className={`shadow-sm transition-transform ${onClick ? "cursor-pointer active:scale-[0.98]" : ""}`}
      onClick={onClick}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex flex-1 items-center gap-2">
            {dragHandle}
            <Map className="size-4 text-muted-foreground" />
            <CardTitle>{name}</CardTitle>
          </div>
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon-sm" className="tap-target rounded-lg ring-1 ring-foreground/8" onClick={onEdit}>
              <Pencil className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="tap-target rounded-lg ring-1 ring-destructive/15 text-destructive hover:bg-destructive/10"
              onClick={onDelete}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {previewUrl ? (
          <div className="mb-3 overflow-hidden rounded-md border border-border">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={previewUrl}
              alt={`Plan de ${name}`}
              className="max-h-32 w-full object-contain bg-muted"
            />
          </div>
        ) : (
          <div className="mb-3 flex h-24 items-center justify-center rounded-md border-2 border-dashed border-border bg-muted/50">
            <span className="text-xs text-muted-foreground">
              Aucun plan — rectangle par défaut
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={planType === "default" ? "outline" : "secondary"}>
              {planType === "default" ? "Pas de plan" : "Plan importé"}
            </Badge>
            {mesureCount !== undefined && mesureCount > 0 && (
              <Badge variant="default" className="tabular-nums">
                {mesureCount} mesure{mesureCount > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {onClick && <ChevronRight className="size-4 text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
}
